import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button, Table} from "react-bootstrap";
import {useAlert} from "react-alert";
import {Loading} from "../../../genericComponents";
import {useConfirm} from "../../../genericComponents/ConfirmDialog";
import {
  deleteCustomerQuestionnaire,
  getCustomerQuestionnaires,
  postUpdateCustomerQuestionnaire,
  putInsertCustomerQuestionnaire,
} from "../../../services/RequestService";
import QuestionnaireForm from "./QuestionnaireForm";
import {QUESTIONNAIRE_TYPE_LABELS, QUESTIONNAIRE_TYPES} from "./questionnaireDefinitions";
import "./QuestionnaireForm.css";

const errorMessage = (error, fallback) => error?.data?.log || error?.data?.message || fallback;

/** Id do questionário mais recente de cada tipo — é o que o app mobile exibe. */
function latestIdsByType(list) {
  const ids = new Set();
  Object.values(QUESTIONNAIRE_TYPES).forEach((type) => {
    const latest = list.find((q) => q.type === type); // lista já vem do mais recente p/ o mais antigo
    if (latest) ids.add(latest.id);
  });
  return ids;
}

/**
 * Questionários de um cliente já cadastrado: lista, cria, edita e exclui.
 * Um cliente pode ter vários questionários de cada tipo.
 */
function CustomerQuestionnaires({customerId, disabled, canDelete}) {
  const alert = useAlert();
  const confirm = useConfirm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  // null = listagem; { type, id?, answers? } = formulário aberto
  const [editing, setEditing] = useState(null);
  const rootRef = useRef(null);
  // o useAlert muda de identidade a cada alerta exibido/removido — não pode disparar recarga da lista
  const alertRef = useRef(alert);
  alertRef.current = alert;

  // ao abrir/fechar o formulário, volta ao topo: o formulário é longo e a tabela é curta —
  // sem isso a tabela fica fora da área visível (tela "em branco") depois de salvar
  useEffect(() => {
    rootRef.current?.scrollIntoView({block: "start"});
  }, [editing]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const {data} = await getCustomerQuestionnaires(customerId);
      setList(data || []);
    } catch (error) {
      alertRef.current.show(errorMessage(error, "Erro ao carregar os questionários."), {type: "error"});
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (customerId) load();
  }, [customerId, load]);

  async function save(answers) {
    try {
      if (editing.id) {
        await postUpdateCustomerQuestionnaire({id: editing.id, answers});
      } else {
        await putInsertCustomerQuestionnaire({customerId, type: editing.type, answers});
      }
      alert.show("Questionário salvo.", {type: "success"});
      setEditing(null);
      load();
    } catch (error) {
      alert.show(errorMessage(error, "Erro ao salvar o questionário."), {type: "error"});
    }
  }

  async function remove(questionnaire) {
    const ok = await confirm(
      `Excluir o questionário de ${QUESTIONNAIRE_TYPE_LABELS[questionnaire.type]} de ${questionnaire.createdAt}?`,
      {title: "Excluir questionário", btnConfirm: "Excluir"}
    );
    if (!ok) return;
    try {
      await deleteCustomerQuestionnaire(questionnaire.id);
      alert.show("Questionário excluído.", {type: "success"});
      load();
    } catch (error) {
      alert.show(errorMessage(error, "Erro ao excluir o questionário."), {type: "error"});
    }
  }

  return <div ref={rootRef}>{editing ? renderForm() : renderList()}</div>;

  function renderForm() {
    return (
      <div>
        <h5 className="mb-0">
          {editing.id ? "Questionário" : "Novo questionário"} — {QUESTIONNAIRE_TYPE_LABELS[editing.type]}
        </h5>
        {editing.createdAt && (
          <small className="text-muted">
            Criado em {editing.createdAt}{editing.createdByName ? ` por ${editing.createdByName}` : ""}
            {editing.updatedAt ? ` · atualizado em ${editing.updatedAt}` : ""}
          </small>
        )}
        <QuestionnaireForm
          key={editing.id || `new-${editing.type}`}
          type={editing.type}
          initialAnswers={editing.answers}
          disabled={disabled}
          submitLabel={editing.id ? "Salvar alterações" : "Salvar questionário"}
          onSubmit={save}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  function renderList() {
    const latestIds = latestIdsByType(list);

    return (
      <Loading loading={loading}>
        <div className="questionnaire-actions mt-0 mb-3">
          {!disabled && Object.values(QUESTIONNAIRE_TYPES).map((type) => (
            <Button key={type} variant="primary" size="sm" onClick={() => setEditing({type})}>
              <i className="fas fa-plus"/> Novo questionário de {QUESTIONNAIRE_TYPE_LABELS[type]}
            </Button>
          ))}
        </div>

        {!loading && list.length === 0 ? (
          <p className="text-muted">Nenhum questionário respondido.</p>
        ) : (
          <Table striped bordered hover size="sm" responsive>
            <thead>
            <tr>
              <th>Tipo</th>
              <th>Criado em</th>
              <th>Criado por</th>
              <th>Atualizado em</th>
              <th style={{width: 1}}/>
            </tr>
            </thead>
            <tbody>
            {list.map((q) => (
              <tr key={q.id}>
                <td>
                  {QUESTIONNAIRE_TYPE_LABELS[q.type]}
                  {latestIds.has(q.id) && (
                    <span className="questionnaire-current" title="O mais recente de cada tipo é o exibido no app">
                      atual
                    </span>
                  )}
                </td>
                <td>{q.createdAt || "-"}</td>
                <td>{q.createdByName || "-"}</td>
                <td>{q.updatedAt || "-"}</td>
                <td className="text-nowrap">
                  <Button variant="outline-primary" size="sm" onClick={() => setEditing(q)}>
                    {disabled ? "Visualizar" : "Abrir"}
                  </Button>
                  {canDelete && !disabled && (
                    <Button variant="outline-danger" size="sm" style={{marginLeft: 4}} onClick={() => remove(q)}
                            title="Excluir">
                      <i className="fas fa-trash"/>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            </tbody>
          </Table>
        )}
      </Loading>
    );
  }
}

export default CustomerQuestionnaires;
